import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { updatePatient } from '../../redux/patientSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import emptykey from '../../assets/img/colorkit (2).svg'; // Adjust the path to your image file     
import { Plus, Edit, X, Trash2 } from 'lucide-react';

const Dependencies = () => {
    const { t } = useTranslation(['contact_page', 'common']);
    const dispatch = useDispatch();
    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);
    const contacts = selectedPatient?.otherContact || [];
    const userRole = localStorage.getItem('Role');
    const patientId = (userRole === 'admin' || userRole === 'doctor')
        ? selectedPatient.patientId
        : localStorage.getItem('ID');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        occupation: '',
        email: '',
        contact: '',
        comment: '',
        address: '',
    });
    const [errors, setErrors] = useState({});
    const [editId, setEditId] = useState(null);

    // Email regex from DoctorRegister.jsx
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Helper function to sanitize and truncate data for form
    const sanitizeFormData = (data) => {
        return {
            firstname: (data.firstname || '').replace(/[^A-Za-zÀ-ÿ\s]/g, '').slice(0, 20).trim(),
            lastname: (data.lastname || '').replace(/[^A-Za-zÀ-ÿ\s]/g, '').slice(0, 20).trim(),
            occupation: (data.occupation || '').replace(/[^A-Za-zÀ-ÿ\s]/g, '').slice(0, 20).trim(),
            email: (data.email || '').replace(/[^A-Za-z0-9@._+-]/g, '').trim(),
            contact: (data.contact || '').toString().replace(/[^0-9]/g, '').slice(0, 10),
            comment: (data.comment || '').replace(/[^A-Za-zÀ-ÿ0-9\s]/g, '').slice(0, 25).trim(),
            address: (data.address || '').replace(/[^A-Za-zÀ-ÿ0-9\s,.-]/g, '').slice(0, 30).trim(),
        };
    };

    // Helper function to truncate text for display
    const truncateText = (text, maxLength = 30) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + '...';
    };

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        // First Name: Required, alphabets + spaces, 2-20 chars
        if (!formData.firstname.trim()) {
            newErrors.firstname = t('contact_page.first_name_required_error');
        } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.firstname)) {
            newErrors.firstname = t('contact_page.first_name_invalid_error');
        }

        // Surname: Required, alphabets + spaces, 2-20 chars
        if (!formData.lastname.trim()) {
            newErrors.lastname = t('contact_page.surname_required_error');
        } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.lastname)) {
            newErrors.lastname = t('contact_page.surname_invalid_error');
        }

        // Occupation: Required, alphabets + spaces, 2-20 chars
        if (!formData.occupation.trim()) {
            newErrors.occupation = t('contact_page.occupation_required_error');
        } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.occupation)) {
            newErrors.occupation = t('contact_page.occupation_invalid_error');
        }

        // Email: Required, valid email format (from DoctorRegister.jsx)
        if (!formData.email.trim()) {
            newErrors.email = t('contact_page.email_required_error');
        } else {
            const email = formData.email.trim();
            // Check total length (RFC 5321: max 254 characters)
            if (email.length > 254) {
                newErrors.email = t('dependencies.email_too_long');
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('contact_page.email_invalid_error');
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                    } else if (localPart.length === 0) {
                        newErrors.email = t('contact_page.email_invalid_error');
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('contact_page.email_invalid_error');
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('dependencies.email_domain_too_long');
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('contact_page.email_invalid_error');
                    }
                }
            }
        }

        // Contact: Required, digits only, 9-10 digits
        if (!formData.contact) {
            newErrors.contact = t('contact_page.contact_required_error');
        } else if (!/^\d{9,10}$/.test(formData.contact.toString())) {
            newErrors.contact = t('contact_page.contact_invalid_error');
        }

        // Comment: Optional, alphabets + numbers + spaces, max 25 chars
        if (formData.comment && formData.comment.length > 25) {
            newErrors.comment = t('contact_page.comment_invalid_error');
        } else if (formData.comment && !/^[A-Za-zÀ-ÿ0-9\s]*$/.test(formData.comment)) {
            newErrors.comment = t('contact_page.comment_invalid_error');
        }

        // Address: Required, letters/numbers/commas/dots/hyphens, 5-30 chars
        if (!formData.address.trim()) {
            newErrors.address = t('contact_page.address_required_error');
        } else if (!/^[A-Za-zÀ-ÿ0-9\s,.-]{5,30}$/.test(formData.address)) {
            newErrors.address = t('contact_page.address_invalid_error');
        }

        return newErrors;
    };

    // Handle input change with real-time validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let sanitizedValue = value;

        // Capitalize first letter for firstname and lastname
        if (name === 'firstname' || name === 'lastname') {
            if (sanitizedValue.length > 0) {
                sanitizedValue = sanitizedValue.charAt(0).toUpperCase() + sanitizedValue.slice(1);
            }
        }

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }

        if (name === 'contact') {
            // Only allow digits, max 10
            sanitizedValue = sanitizedValue.replace(/[^0-9]/g, '').slice(0, 10);
        } else if (name === 'firstname' || name === 'lastname') {
            // Only allow letters (incl. accented) and spaces, max 20 chars
            sanitizedValue = sanitizedValue.replace(/[^A-Za-zÀ-ÿ\s]/g, '').slice(0, 20);
        } else if (name === 'occupation') {
            // Only allow letters (incl. accented) and spaces, max 20 chars
            sanitizedValue = value.replace(/[^A-Za-zÀ-ÿ\s]/g, '').slice(0, 20);
        } else if (name === 'address') {
            // Allow letters (incl. accented), numbers, spaces, commas, periods, and hyphens only, max 30 chars
            sanitizedValue = value.replace(/[^A-Za-zÀ-ÿ0-9\s,.-]/g, '').slice(0, 30);
        } else if (name === 'comment') {
            // Only allow letters (incl. accented), numbers, and spaces, max 25 chars
            sanitizedValue = value.replace(/[^A-Za-zÀ-ÿ0-9\s]/g, '').slice(0, 25);
        } else if (name === 'email') {
            // Allow valid email characters only (letters, numbers, @, ., -, _, +) - from DoctorRegister.jsx
            sanitizedValue = value.replace(/[^A-Za-z0-9@._+-]/g, '');
        }

        setFormData({ ...formData, [name]: sanitizedValue });
    };

    // Handle blur event for validation
    const handleBlur = (e) => {
        const { name } = e.target;
        const newErrors = { ...errors };

        // Validate individual field
        if (name === 'firstname') {
            if (!formData.firstname.trim()) {
                newErrors.firstname = t('contact_page.first_name_required_error');
            } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.firstname)) {
                newErrors.firstname = t('contact_page.first_name_invalid_error');
            } else {
                delete newErrors.firstname;
            }
        } else if (name === 'lastname') {
            if (!formData.lastname.trim()) {
                newErrors.lastname = t('contact_page.surname_required_error');
            } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.lastname)) {
                newErrors.lastname = t('contact_page.surname_invalid_error');
            } else {
                delete newErrors.lastname;
            }
        } else if (name === 'occupation') {
            if (!formData.occupation.trim()) {
                newErrors.occupation = t('contact_page.occupation_required_error');
            } else if (!/^[A-Za-zÀ-ÿ\s]{2,20}$/.test(formData.occupation)) {
                newErrors.occupation = t('contact_page.occupation_invalid_error');
            } else {
                delete newErrors.occupation;
            }
        } else if (name === 'email') {
            if (!formData.email.trim()) {
                newErrors.email = t('contact_page.email_required_error');
            } else {
                const email = formData.email.trim();
                if (email.length > 254) {
                    newErrors.email = t('dependencies.email_too_long');
                } else {
                    const parts = email.split('@');
                    if (parts.length !== 2) {
                        newErrors.email = t('contact_page.email_invalid_error');
                    } else {
                        const [localPart, domainPart] = parts;
                        if (localPart.length > 64) {
                            newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                        } else if (localPart.length === 0) {
                            newErrors.email = t('contact_page.email_invalid_error');
                        } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                            newErrors.email = t('contact_page.email_invalid_error');
                        } else if (domainPart.length > 253) {
                            newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
                        } else if (!emailRegex.test(email)) {
                            newErrors.email = t('contact_page.email_invalid_error');
                        } else {
                            delete newErrors.email;
                        }
                    }
                }
            }
        } else if (name === 'contact') {
            if (!formData.contact) {
                newErrors.contact = t('contact_page.contact_required_error');
            } else if (!/^\d{9,10}$/.test(formData.contact.toString())) {
                newErrors.contact = t('contact_page.contact_invalid_error');
            } else {
                delete newErrors.contact;
            }
        } else if (name === 'comment') {
            if (formData.comment && formData.comment.length > 25) {
                newErrors.comment = t('contact_page.comment_invalid_error');
            } else if (formData.comment && !/^[A-Za-zÀ-ÿ0-9\s]*$/.test(formData.comment)) {
                newErrors.comment = t('contact_page.comment_invalid_error');
            } else {
                delete newErrors.comment;
            }
        } else if (name === 'address') {
            if (!formData.address.trim()) {
                newErrors.address = t('contact_page.address_required_error');
            } else if (!/^[A-Za-zÀ-ÿ0-9\s,.-]{5,30}$/.test(formData.address)) {
                newErrors.address = t('contact_page.address_invalid_error');
            } else {
                delete newErrors.address;
            }
        }

        setErrors(newErrors);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const payload = {
            otherContact: editId
                ? contacts.map((contact, index) =>
                    index + 1 === editId ? { ...formData } : contact
                )
                : [...contacts, { ...formData }],
        };

        const result = await Swal.fire({
            title: editId ? t('contact_page.confirm_update') : t('contact_page.confirm_add'),
            text: editId ? t('contact_page.confirm_update_text') : t('contact_page.confirm_add_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: editId ? t('contact_page.update_btn') : t('contact_page.add_btn'),
            cancelButtonText: t('contact_page.cancel_btn'),
        });

        if (result.isConfirmed) {
            try {
                await dispatch(
                    updatePatient({
                        patientid: patientId,
                        payload,
                        keypass: editId ? 'editdependency' : 'adddependency',
                    })
                ).unwrap();

                toast.success(editId ? t('contact_page.update_success') : t('contact_page.add_success'));

                setFormData({
                    firstname: '',
                    lastname: '',
                    occupation: '',
                    email: '',
                    contact: '',
                    comment: '',
                    address: '',
                });
                setErrors({});
                setIsModalOpen(false);
                setEditId(null);
            } catch (err) {
                toast.warn(t('contact_page.api_error'));
            }
        }
    };

    // Handle edit button click - sanitize data before loading
    const handleEditClick = (contact, index) => {
        const sanitizedData = sanitizeFormData(contact);
        setFormData(sanitizedData);
        setErrors({});
        setEditId(index + 1);
        setIsModalOpen(true);
    };

    // Handle delete button click
    const handleDeleteClick = async (index) => {
        const result = await Swal.fire({
            title: t('contact_page.delete_confirm_title'),
            text: t('contact_page.delete_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('contact_page.delete_btn'),
            cancelButtonText: t('contact_page.cancel_btn'),
        });

        if (result.isConfirmed) {
            const payload = {
                otherContact: contacts.filter((_, i) => i !== index),
            };

            try {
                await dispatch(
                    updatePatient({ patientid: patientId, payload, keypass: 'deletedependency' })
                ).unwrap();

                toast.success(t('contact_page.delete_success'));
            } catch (err) {
                toast.warn(t('contact_page.api_error'));
            }
        }
    };

    // Handle add button click
    const handleAddClick = () => {
        setFormData({
            firstname: '',
            lastname: '',
            occupation: '',
            email: '',
            contact: '',
            comment: '',
            address: '',
        });
        setErrors({});
        setEditId(null);
        setIsModalOpen(true);
    };

    return (
        <div className="mt-4">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-between items-center mb-6">
                <h2
                    className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                    id="patient-profile-dependencies-heading"
                    data-testid="dependencies-heading"
                >
                    {t('contact_page.heading')}
                </h2>
                <button
                    id="patient-profile-add-dependency-button"
                    onClick={handleAddClick}
                    className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer w-full sm:w-auto justify-center"
                    disabled={isLoading}
                    data-testid="add-dependency-button"
                >
                    <Plus className="w-5 h-5" />
                    <span className="relative z-10">{t('contact_page.add_button')}</span>
                    {/* <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div> */}
                </button>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    id="patient-profile-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="patient-profile-modal-title"
                                data-testid="modal-title"
                            >
                                {editId ? t('contact_page.edit_contact_title') : t('contact_page.add_contact_title')}
                            </h3>
                            <button
                                id="patient-profile-modal-close-button"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setErrors({});
                                    setEditId(null);
                                }}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="patient-profile-dependency-form" onSubmit={handleSubmit} data-testid="dependency-form">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-firstname-input"
                                            id="patient-profile-firstname-label"
                                            data-testid="firstname-label"
                                        >
                                            {t('contact_page.first_name_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="patient-profile-firstname-input"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.first_name_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.firstname
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            required
                                            maxLength={20}
                                            data-testid="firstname-input"
                                        />
                                        {errors.firstname && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-firstname-error"
                                                data-testid="firstname-error"
                                            >
                                                {errors.firstname}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-lastname-input"
                                            id="patient-profile-lastname-label"
                                            data-testid="lastname-label"
                                        >
                                            {t('contact_page.surname_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="patient-profile-lastname-input"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.surname_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.lastname
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            required
                                            maxLength={20}
                                            data-testid="lastname-input"
                                        />
                                        {errors.lastname && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-lastname-error"
                                                data-testid="lastname-error"
                                            >
                                                {errors.lastname}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-occupation-input"
                                            id="patient-profile-occupation-label"
                                            data-testid="occupation-label"
                                        >
                                            {t('contact_page.occupation_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="patient-profile-occupation-input"
                                            name="occupation"
                                            value={formData.occupation}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.occupation_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.occupation
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            required
                                            maxLength={20}
                                            data-testid="occupation-input"
                                        />
                                        {errors.occupation && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-occupation-error"
                                                data-testid="occupation-error"
                                            >
                                                {errors.occupation}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-email-input"
                                            id="patient-profile-email-label"
                                            data-testid="email-label"
                                        >
                                            {t('contact_page.email_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="patient-profile-email-input"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.email_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.email
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            required
                                            maxLength={254}
                                            data-testid="email-input"
                                        />
                                        {errors.email && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-email-error"
                                                data-testid="email-error"
                                            >
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-contact-input"
                                            id="patient-profile-contact-label"
                                            data-testid="contact-label"
                                        >
                                            {t('contact_page.contact_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="patient-profile-contact-input"
                                            name="contact"
                                            value={formData.contact}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.contact_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${errors.contact
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            required
                                            maxLength={10}
                                            data-testid="contact-input"
                                        />
                                        {errors.contact && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-contact-error"
                                                data-testid="contact-error"
                                            >
                                                {errors.contact}
                                            </p>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-comment-input"
                                            id="patient-profile-comment-label"
                                            data-testid="comment-label"
                                        >
                                            {t('contact_page.comment_label')}
                                        </label>
                                        <input
                                            type="text"
                                            id="patient-profile-comment-input"
                                            name="comment"
                                            value={formData.comment}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.comment_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.comment
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            maxLength={25}
                                            data-testid="comment-input"
                                        />
                                        {errors.comment && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-comment-error"
                                                data-testid="comment-error"
                                            >
                                                {errors.comment}
                                            </p>
                                        )}
                                    </div>
                                    <div data-testid="address-section" className="sm:col-span-2">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="patient-profile-address-input"
                                            id="patient-profile-address-label"
                                            data-testid="address-label"
                                        >
                                            {t('contact_page.address_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="patient-profile-address-input"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder={t('contact_page.address_placeholder')}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.address
                                                    ? 'border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                }`}
                                            rows="4"
                                            required
                                            maxLength={30}
                                            data-testid="address-input"
                                        />
                                        {errors.address && (
                                            <p
                                                className="mt-2 text-xs text-red-500 font-medium"
                                                id="patient-profile-address-error"
                                                data-testid="address-error"
                                            >
                                                {errors.address}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="patient-profile-form-cancel-button"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setErrors({});
                                    setEditId(null);
                                }}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isLoading}
                                data-testid="form-cancel-button"
                            >
                                {t('contact_page.cancel_button')}
                            </button>
                            <button
                                type="submit"
                                id="patient-profile-form-submit-button"
                                form="patient-profile-dependency-form"
                                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm flex items-center gap-2 cursor-pointer"
                                disabled={isLoading}
                                data-testid="form-submit-button"
                            >
                                {isLoading && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isLoading
                                    ? t('contact_page.loading_button')
                                    : editId
                                        ? t('contact_page.update_button')
                                        : t('contact_page.add_button_form')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Cards */}
            <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6"
                id="patient-profile-contact-list"
                data-testid="contact-list"
            >
                {contacts.length === 0 ? (
                    <div
                        className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8"
                        id="patient-profile-no-dependencies-message"
                        data-testid="no-dependencies-message"
                    >
                        <img
                            src={emptykey}
                            alt={t('contact_page.no_dependencies_found_alt', 'No Dependencies Found')}
                            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                            data-testid="no-dependencies-image"
                        />
                        <p
                            className="text-gray-500 text-center text-sm sm:text-base md:text-lg"
                            data-testid="no-dependencies-text"
                        >
                            {t('contact_page.no_dependency')} {/* Fallback to original key if 'customPrice.empty' is not defined */}
                        </p>
                    </div>
                ) : (
                    contacts.map((contact, index) => (
                        <div
                            key={index}
                            className="group relative flex flex-col gap-3 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-4 hover:scale-105"
                            id={`patient-profile-contact-card-${index}`}
                            data-testid={`contact-card-${index}`}
                        >
                            {/* Header */}
                            <div className="flex justify-between items-start">
                                <h3
                                    className="text-sm sm:text-base font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors break-words"
                                    id={`patient-profile-contact-name-${index}`}
                                    data-testid={`contact-name-${index}`}
                                >
                                    {truncateText((contact.firstname || '') + ' ' + (contact.lastname || ''), 30)}
                                </h3>
                                <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-opacity">
                                    <button
                                        id={`patient-profile-edit-button-${index}`}
                                        onClick={() => handleEditClick(contact, index)}
                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        disabled={isLoading}
                                        data-testid={`edit-button-${index}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        id={`patient-profile-delete-button-${index}`}
                                        onClick={() => handleDeleteClick(index)}
                                        className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        disabled={isLoading}
                                        data-testid={`delete-button-${index}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="space-y-2">
                                {[
                                    {
                                        label: t('contact_page.occupation_label'),
                                        value: contact.occupation,
                                        maxLength: 30,
                                        testId: `occupation-${index}`,
                                        id: `patient-profile-occupation-${index}`,
                                    },
                                    {
                                        label: t('contact_page.email_label'),
                                        value: contact.email,
                                        maxLength: 40,
                                        testId: `email-${index}`,
                                        id: `patient-profile-email-${index}`,
                                    },
                                    {
                                        label: t('contact_page.contact_label'),
                                        value: contact.contact,
                                        maxLength: 15,
                                        testId: `contact-${index}`,
                                        id: `patient-profile-contact-${index}`,
                                    },
                                    {
                                        label: t('contact_page.comment_label'),
                                        value: contact.comment || t('contact_page.no_comment'),
                                        maxLength: 30,
                                        testId: `comment-${index}`,
                                        id: `patient-profile-comment-${index}`,
                                    },
                                    {
                                        label: t('contact_page.address_label'),
                                        value: contact.address,
                                        maxLength: 40,
                                        testId: `address-${index}`,
                                        id: `patient-profile-address-${index}`,
                                    },
                                ].map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="grid grid-cols-[120px_1fr] gap-2 text-xs sm:grid-cols-[150px_1fr] sm:text-sm"
                                        id={`patient-profile-contact-detail-${index}-${idx}`}
                                        data-testid={`contact-detail-${index}-${idx}`}
                                    >
                                        <span className="font-medium text-gray-600">{item.label}:</span>
                                        <span
                                            className="break-words text-gray-500"
                                            id={item.id}
                                            data-testid={item.testId}
                                            title={item.value}
                                        >
                                            {truncateText(item.value || '', item.maxLength)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default Dependencies;